import React, { useState } from "react";
import {
  Stack,
  TextField,
  Button,
  CircularProgress,
  Backdrop,
  Autocomplete,
  Tooltip,
  IconButton,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import SearchArticlesModalResult from "./SearchArticlesModalResult";

import "./css/SearchArticle.css";

function SearchArticles() {
  const { t } = useTranslation(["Articles"]);
  const userState = useSelector((state) => state.user);

  const [keywords, setKeywords] = useState([]);
  const [inputValue, setInputValue] = useState(""); // 👈 NEW
  const [publishDate, setPublishDate] = useState(
    dayjs().subtract(1, "month").format("YYYY-MM-DD")
  );
  const [endPublishDate, setEndPublishDate] = useState(
    dayjs().format("YYYY-MM-DD")
  );
  const [searchArticle, setSearchArticle] = useState([]);
  const [open, setOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const handleSearch = async () => {
    try {
      setIsSearching(true);
      await delay(500);

      if (keywords.length > 0) {
        await handleSearchByKeywords();
      } else {
        await handleSearchByDate();
      }
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchByKeywords = async () => {
    const data = {
      username: userState.username,
      keywords: keywords,
      publicationDate: publishDate || null,
      endPublicationDate: endPublishDate || null,
    };

    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/article/SearchArticleByKeyWords`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userState.token}`,
        },
        body: JSON.stringify(data),
      }
    );

    const result = await response.json();
    setSearchArticle(result.length > 0 ? result : []);
    setOpen(true);
  };

  const handleSearchByDate = async () => {
    const data = {
      username: userState.username,
      publicationDate: publishDate || null,
      endPublicationDate: endPublishDate || null,
    };

    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/article/SearchArticleByDate`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userState.token}`,
        },
        body: JSON.stringify(data),
      }
    );

    const result = await response.json();
    setSearchArticle(result);
    setOpen(true);
  };

  return (
    <>
      <Stack
        spacing={2}
        direction="row"
        alignItems="center"
        className="Filter__Search"
        flexWrap="wrap"
      >
        {/* Date Range */}
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack direction="row" spacing={2} alignItems="center">
            <div className="Filter__date__datePicker">
              <label>{t("StartDate")}</label>
              <DatePicker
                value={dayjs(publishDate)}
                onChange={(newValue) =>
                  setPublishDate(
                    newValue ? newValue.format("YYYY-MM-DD") : null
                  )
                }
                sx={{ minWidth: 150 }}
              />
            </div>

            <div className="Filter__date__datePicker">
              <label>{t("EndDate")}</label>
              <DatePicker
                value={dayjs(endPublishDate)}
                onChange={(newValue) =>
                  setEndPublishDate(
                    newValue ? newValue.format("YYYY-MM-DD") : null
                  )
                }
                sx={{ minWidth: 150 }}
              />
            </div>
          </Stack>
        </LocalizationProvider>

        {/* Keywords Autocomplete */}
        <Autocomplete
          multiple
          freeSolo
          options={[]}
          value={keywords}
          inputValue={inputValue}
          onInputChange={(event, newInputValue) =>
            setInputValue(newInputValue)
          }
          onChange={(event, newValue) => setKeywords(newValue)}
          onBlur={() => {
            const trimmed = inputValue.trim();
            if (trimmed !== "" && !keywords.includes(trimmed)) {
              setKeywords((prev) => [...prev, trimmed]);
            }
            setInputValue("");
          }}
          sx={{ minWidth: 250, flex: 1 }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("Keywords")}
              placeholder={t("Type a keyword and press Enter")}
              helperText={t(
                "You can add multiple keywords to refine your search"
              )}
              sx={{ height: 56 }}
              InputProps={{
                ...params.InputProps,
                sx: { height: 56 },
                endAdornment: (
                  <>
                    <Tooltip
                      title={t(
                        "Type a word and press Enter to add it as a keyword"
                      )}
                    >
                      <IconButton size="small">
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        {/* Search Button */}
        <Button
          variant="contained"
          sx={{ backgroundColor: "#FFBC0F", color: "black", height: 56 }}
          onClick={handleSearch}
          disabled={isSearching}
          startIcon={
            isSearching ? (
              <CircularProgress size={20} color="inherit" />
            ) : null
          }
        >
          {isSearching ? t("Searching...") : t("Search")}
        </Button>
      </Stack>

      {/* Modal Result */}
      <SearchArticlesModalResult
        open={open}
        close={() => setOpen(false)}
        searchArticle={searchArticle}
      />

      {/* Backdrop Loading */}
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: 9999,
          backgroundColor: "rgba(0, 0, 0, 0.7)",
        }}
        open={isSearching}
      >
        <CircularProgress color="primary" size={60} />
      </Backdrop>
    </>
  );
}

export default SearchArticles;

